import { useForm, useFieldArray } from "react-hook-form";
import { useEffect, useRef, useState, useContext } from "react";
import { GdkButtonSwitch } from "@gdk/button-switch";
import "@gdk/button-switch/dist/styles/gdk-button-switch.min.css";
import { GdkModal } from "@gdk/modal";
import { GdkToasts } from "@gdk/toasts";
import PageHeader from "../PageHeader/PageHeader";
import Layout from "../Layout";
import { EMAIL_WITH_TLD_REGEX } from "../../constants/ValidationConstants";
import { searchAgencies } from "../../api/search";
import { addToList, updateList, retrieveList } from "../../api/admin";
import './ClientAlertEmailPreferencesForm.css';
import { GdsFlexibleContainer } from "@gds/web-components/react";
import { UserContext } from "../../context/UserContextProvider";

const MAX_RECIPIENTS = 10;

const defaultRecipient = () => ({ email: '', daily: false, biweekly: false });

const defaultFormValues = {
    optIn: false,
    recipients: [],
};

export default function ClientAlertEmailPreferencesForm() {
    const user = useContext(UserContext);
    const [agencyInfo, setAgencyInfo] = useState(null);
    const [existingRecordId, setExistingRecordId] = useState(null);

    const [initialValues, setInitialValues] = useState(defaultFormValues);
    const [enableSaveButton, setEnableSaveButton] = useState(false);
    const [enableCancelButton, setEnableCancelButton] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [newRecipient, setNewRecipient] = useState(defaultRecipient());
    const [editingIndex, setEditingIndex] = useState(null);
    const [editingRecipient, setEditingRecipient] = useState(null);
    const [addEmailErrorMsg, setAddEmailErrorMsg] = useState(null);
    const [addFreqError, setAddFreqError] = useState(false);
    const [editEmailErrorMsg, setEditEmailErrorMsg] = useState(null);
    const [editFreqError, setEditFreqError] = useState(false);

    const onRetrieveListSuccess = (response) => {
        const record = response?.domain?.[0];
        if (!record?.id) {
            setExistingRecordId(null);
            resetForm(defaultFormValues);
            return;
        }

        setExistingRecordId(record.id);

        const prefs = record.applicationExtensionData?.AdminList?.clientPreferences;
        const loadedValues = {
            optIn: prefs?.optInStatus ?? false,
            recipients: (prefs?.recipients ?? []).map(r => ({
                email: r.emailAddress ?? '',
                daily: r.cadences?.includes('Daily') ?? false,
                biweekly: r.cadences?.includes('Biweekly') ?? false
            })),
        };

        const currentOptIn = getValues('optIn');
        setInitialValues(loadedValues);
        reset(loadedValues);
        if (loadedValues.optIn !== currentOptIn) {
            gdkButtonSwitchRef.current?.toggleSwitch();
        }
    };

    const onRetrieveListError = (error) => {
        console.error(error);
        showErrorToast('Failed to load client preferences. Please refresh the page to try again.');
    };

    useEffect(() => {
        if (!user?.agencyId) return;

        retrieveList(
            { listType: 'ClientPreferences', listStatus: true, sob: user.agencyId },
            onRetrieveListSuccess,
            onRetrieveListError
        );

        searchAgencies({ agencyId: user.agencyId }, (response) => {
            const agency = response?.find(a => a.agencyId === user.agencyId);
            if (agency) {
                setAgencyInfo({
                    agencyId: user.agencyId,
                    agentId: user.agentId,
                    name: agency.name,
                    fein: agency.federalEmployerIdentificationNumber,
                    npn: agency.agencyNationalProducerNumber,
                });
            }
        });
    }, []);

    const gdkButtonSwitchRef = useRef(null);
    const gdkModalRef = useRef(null);
    const successToastRef = useRef(null);
    const errorToastRef = useRef(null);
    const successToastContentRef = useRef(null);
    const errorToastContentRef = useRef(null);
    const addRecipientModalRef = useRef(null);
    const editRecipientModalRef = useRef(null);
    const dailyCheckboxRef = useRef(null);
    const biweeklyCheckboxRef = useRef(null);
    const editDailyRef = useRef(null);
    const editBiweeklyRef = useRef(null);
    const addRadioGroupRef = useRef(null);
    const editRadioGroupRef = useRef(null);

    const { register, handleSubmit, watch, reset, getValues, setValue, control } = useForm({
        mode: 'onChange',
        defaultValues: defaultFormValues,
    });

    const { fields, append, remove, update } = useFieldArray({ control, name: 'recipients' });

    const watchOptIn = watch('optIn');
    const watchRecipients = watch('recipients');

    useEffect(() => {
        gdkModalRef.current = new GdkModal({
            content: '#cancel-confirm-modal',
            hideCloseButton: false,
            onClosed: () => setShowCancelModal(false),
        });
    }, []);

    useEffect(() => {
        successToastRef.current = new GdkToasts({
            content: '#client-prefs-success-toast',
            floatTime: 'short',
            closeBtn: true,
            baseTimer: true,
        });
        errorToastRef.current = new GdkToasts({
            content: '#client-prefs-error-toast',
            floatTime: 'short',
            closeBtn: true,
            baseTimer: true,
        });
    }, []);

    useEffect(() => {
        gdkButtonSwitchRef.current = new GdkButtonSwitch({
            content: '#opt-in-toggle',
            initiallyChecked: getValues('optIn'),
            onSwitchChange: (checked) => {
                setValue('optIn', checked, { shouldValidate: true });
            },
        });
    }, []);

    useEffect(() => {
        if (showCancelModal && gdkModalRef.current?.show) {
            gdkModalRef.current.show();
        }
        if (!showCancelModal && gdkModalRef.current?.hide) {
            gdkModalRef.current.hide();
        }
    }, [showCancelModal]);

    useEffect(() => {
        if (watchOptIn) {
            const hideShowComponent = document.querySelector("gds-show-hide");
            if (hideShowComponent && typeof hideShowComponent.toggle === "function") {
                hideShowComponent.toggle();
            }
        }
    }, [watchOptIn]);

    useEffect(() => {
        const recipientsHasChanged = JSON.stringify(watchRecipients) !== JSON.stringify(initialValues.recipients);
        const optInHasChanged = watchOptIn !== initialValues.optIn;

        if ((watchOptIn && recipientsHasChanged && watchRecipients.length > 0)
            || (watchOptIn && optInHasChanged && watchRecipients.length > 0)
            || !watchOptIn && optInHasChanged) {
            setEnableSaveButton(true);
        }
        else {
            setEnableSaveButton(false);
        }
    }, [watchRecipients, watchOptIn, initialValues]);

    useEffect(() => {
        const recipientsHasChanged = JSON.stringify(watchRecipients) !== JSON.stringify(initialValues.recipients);
        const optInHasChanged = watchOptIn !== initialValues.optIn;

        if (recipientsHasChanged || optInHasChanged) {
            setEnableCancelButton(true);
        }
        else {
            setEnableCancelButton(false);
        }
    }, [watchRecipients, watchOptIn, initialValues]);

    useEffect(() => {
        if (editingIndex === null) return;
        if (editDailyRef.current) editDailyRef.current.checked = editingRecipient?.daily ?? false;
        if (editBiweeklyRef.current) editBiweeklyRef.current.checked = editingRecipient?.biweekly ?? false;
    }, [editingIndex]);

    useEffect(() => {
        const el = addRadioGroupRef.current;
        if (!el) return;
        const handler = () => setAddFreqError(false);
        el.addEventListener('click', handler);
        el.addEventListener('value-change', handler);
        return () => {
            el.removeEventListener('click', handler);
            el.removeEventListener('value-change', handler);
        };
    }, []);

    useEffect(() => {
        const el = editRadioGroupRef.current;
        if (!el) return;
        const handler = () => setEditFreqError(false);
        el.addEventListener('click', handler);
        el.addEventListener('value-change', handler);
        return () => {
            el.removeEventListener('click', handler);
            el.removeEventListener('value-change', handler);
        };
    }, []);

    const showSuccessToast = (msg) => {
        if (successToastContentRef.current) successToastContentRef.current.textContent = msg;
        successToastRef.current?.show();
    };

    const showErrorToast = (msg) => {
        if (errorToastContentRef.current) errorToastContentRef.current.textContent = msg;
        errorToastRef.current?.show();
    };

    const resetForm = (values) => {
        const currentOptIn = getValues('optIn');
        setInitialValues(values);
        reset(values);
        if (values.optIn !== currentOptIn) {
            gdkButtonSwitchRef.current?.toggleSwitch();
        }
    };

    const onSave = (values) => {
        if (!agencyInfo) return;

        const requestBody = {
            "sob": agencyInfo?.agencyId,
            "name": agencyInfo?.name,
            "nationalProducerNumber": agencyInfo?.npn,
            "federalEmployerIdentificationNumber": agencyInfo?.fein,
            "profileType": "Agency",
            "listType": "ClientPreferences",
            "listStatus": true,
            "modifiedByUserId": agencyInfo?.agentId,
            "modifiedDate": new Date().toLocaleDateString('en-CA'),
            "clientPreferences": {
                "optInStatus": values.optIn,
                "recipients": []
            }
        };

        for (const recipient of values.recipients) {
            requestBody.clientPreferences.recipients.push({
                emailAddress: recipient.email,
                cadences: [
                    ...(recipient.daily ? ['Daily'] : []),
                    ...(recipient.biweekly ? ['Biweekly'] : [])
                ]
            });
        }

        const onSaveSuccess = () => {
            if (values.optIn) {
                resetForm(values);
            } else {
                resetForm(defaultFormValues);
            }
            retrieveList(
                { listType: 'ClientPreferences', listStatus: true, sob: user.agencyId },
                onRetrieveListSuccess,
                onRetrieveListError
            );
            showSuccessToast(values.optIn ? 'You are opted into client alert emails.' : 'You are opted out of client alert emails.');
        };

        const onSaveError = (error) => {
            console.error(error);
            showErrorToast(values.optIn
                ? 'Agency failed to be opted into client alert emails. Please try again later.'
                : 'Agency failed to be opted out of client alert emails. Please try again later.');
        };

        const onUpdateSuccess = () => {
            if (values.optIn) {
                resetForm(values);
            } else {
                resetForm(defaultFormValues);
            }
            retrieveList(
                { listType: 'ClientPreferences', listStatus: true, sob: user.agencyId },
                onRetrieveListSuccess,
                onRetrieveListError
            );
            showSuccessToast("Agency's client preferences have been updated.");
        };

        const onUpdateError = (error) => {
            console.error(error);
            showErrorToast("Agency's client preferences failed to be saved. Please try again later.");
        };

        if (existingRecordId) {
            updateList({ ...requestBody, id: existingRecordId }, onUpdateSuccess, onUpdateError);
        } else {
            addToList(requestBody, onSaveSuccess, onSaveError);
        }
    };

    const onCancelClick = () => setShowCancelModal(true);

    const onDiscardChanges = () => {
        resetForm(initialValues);
        setShowCancelModal(false);
    };

    const onReturnToForm = () => setShowCancelModal(false);

    const onEditRecipient = (field, index) => {
        setEditEmailErrorMsg(null);
        setEditFreqError(false);
        setEditingIndex(index);
        setEditingRecipient({ email: field.email, daily: field.daily, biweekly: field.biweekly });
        editRecipientModalRef.current.setAttribute("open", "");
        editRecipientModalRef.current.removeAttribute("overlayshouldclosemodal");
    };

    const onCancelEdit = () => {
        setEditEmailErrorMsg(null);
        setEditFreqError(false);
        setTimeout(() => {
            editRecipientModalRef.current.removeAttribute("open");
        }, 100);
        setEditingIndex(null);
        setEditingRecipient(null);
    };

    const onSaveEdit = (index) => {
        const email = editingRecipient?.email?.trim() ?? '';
        const daily = editDailyRef.current?.checked ?? false;
        const biweekly = editBiweeklyRef.current?.checked ?? false;

        let hasError = false;

        if (!email || !EMAIL_WITH_TLD_REGEX.test(email)) {
            setEditEmailErrorMsg('Please enter a valid email address.');
            hasError = true;
        } else if (fields.some((f, i) => i !== index && f.email.toLowerCase() === email.toLowerCase())) {
            setEditEmailErrorMsg('This email address is already in the recipient list.');
            hasError = true;
        } else {
            setEditEmailErrorMsg(null);
        }

        if (!daily && !biweekly) {
            setEditFreqError(true);
            hasError = true;
        } else {
            setEditFreqError(false);
        }

        if (hasError) return;

        update(index, { email, daily, biweekly });
        setTimeout(() => {
            editRecipientModalRef.current.removeAttribute("open");
        }, 100);
        setEditingIndex(null);
        setEditingRecipient(null);
    };

    const openAddRecipientModal = () => {
        setAddEmailErrorMsg(null);
        setAddFreqError(false);
        addRecipientModalRef.current.setAttribute("open", "");
        addRecipientModalRef.current.removeAttribute("overlayshouldclosemodal");
    };

    const cancelAddRecipientModal = () => {
        setAddEmailErrorMsg(null);
        setAddFreqError(false);
        setNewRecipient(defaultRecipient());
        if (dailyCheckboxRef.current) dailyCheckboxRef.current.checked = false;
        if (biweeklyCheckboxRef.current) biweeklyCheckboxRef.current.checked = false;
        setTimeout(() => {
            addRecipientModalRef.current.removeAttribute("open");
        }, 100);
    };

    const onAddRecipient = () => {
        const email = newRecipient.email.trim();
        const daily = dailyCheckboxRef.current?.checked ?? false;
        const biweekly = biweeklyCheckboxRef.current?.checked ?? false;

        let hasError = false;

        if (!email || !EMAIL_WITH_TLD_REGEX.test(email)) {
            setAddEmailErrorMsg('Please enter a valid email address.');
            hasError = true;
        } else if (fields.some(f => f.email.toLowerCase() === email.toLowerCase())) {
            setAddEmailErrorMsg('This email address is already in the recipient list.');
            hasError = true;
        } else {
            setAddEmailErrorMsg(null);
        }

        if (!daily && !biweekly) {
            setAddFreqError(true);
            hasError = true;
        } else {
            setAddFreqError(false);
        }

        if (hasError) return;

        append({ email, daily, biweekly });
        setNewRecipient(defaultRecipient());
        if (dailyCheckboxRef.current) dailyCheckboxRef.current.checked = false;
        if (biweeklyCheckboxRef.current) biweeklyCheckboxRef.current.checked = false;
        cancelAddRecipientModal();
    };

    const addNewRecipientContainer = () => {
        const atLimit = fields.length >= MAX_RECIPIENTS;

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-m)', paddingBottom: 'var(--spacing-l)', opacity: atLimit ? 0.5 : 1, pointerEvents: atLimit ? 'none' : 'auto' }}>
                <GdsFlexibleContainer style={{ overflow: 'hidden' }}>
                    <gds-banner color="brand">
                        <div className="section-content" style={{ display: 'flex', alignItems: 'center', padding: "var(--spacing-l)" }}>
                            <gds-component-header>
                                {atLimit ? `Add New Recipient (Limit of ${MAX_RECIPIENTS} Reached)` : 'Add New Recipient'}
                            </gds-component-header>
                            {!atLimit && (
                                <div className="client-email-preferences-action-buttons" style={{ marginLeft: 'auto' }}>
                                    <gds-icon-button
                                        icon-type="expand"
                                        size="medium"
                                        onClick={openAddRecipientModal}
                                    />
                                </div>
                            )}
                        </div>
                    </gds-banner>
                </GdsFlexibleContainer>
            </div>
        );
    };

    const existingRecipientContainer = (field, index) => {
        return (
            <div key={field.id} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-m)', marginBottom: 'var(--spacing-l)' }}>
                <GdsFlexibleContainer style={{ padding: "var(--spacing-l)" }}>
                    <div className="section-content" style={{ display: 'flex', alignItems: 'center' }}>
                        <gds-component-header>
                            {field.email}
                            <div slot="subtext" style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '.75rem' }}>
                                {field.daily && <gds-chips type="teal">Daily</gds-chips>}
                                {field.biweekly && <gds-chips type="teal">Bi-weekly</gds-chips>}
                            </div>
                        </gds-component-header>
                        <div className="client-email-preferences-action-buttons" style={{ marginLeft: 'auto' }}>
                            <gds-icon-button
                                icon-type="trash"
                                size="large"
                                style={{ '--icon-button-default-background-color': 'transparent' }}
                                onClick={() => remove(index)}
                            />
                            <gds-icon-button
                                icon-type="edit"
                                size="large"
                                style={{ '--icon-button-default-background-color': 'transparent' }}
                                onClick={() => onEditRecipient(field, index)}
                            />
                        </div>
                    </div>
                </GdsFlexibleContainer>
            </div>
        );
    };

    return (
        <Layout>
            <div id="client-prefs-success-toast" className="toasts-container">
                <div className="toasts toasts--confirmation">
                    <div><span className="icon-confirmation" /></div>
                    <output>
                        <div className="toasts--heading">Success</div>
                        <div className="toasts--content" ref={successToastContentRef}>Your preferences have been saved.</div>
                    </output>
                    <button className="toasts--close-btn icon-close">
                        <div id="toasts--base-timer" />
                    </button>
                </div>
            </div>
            <div id="client-prefs-error-toast" className="toasts-container">
                <div className="toasts toasts--update">
                    <div><span className="icon-info" /></div>
                    <output>
                        <div className="toasts--heading">Error</div>
                        <div className="toasts--content" ref={errorToastContentRef}>Something went wrong. Please try again.</div>
                    </output>
                    <button className="toasts--close-btn icon-close">
                        <div id="toasts--base-timer" />
                    </button>
                </div>
            </div>
            <PageHeader header="Client Alert Email Preferences">
                <p className="subheader">Manage email recipients and their notification frequency for client activity alerts.</p>
            </PageHeader>

            <div className="container margin-top">
                <form onSubmit={handleSubmit(onSave)}>
                    <div className="form-field">
                        <p className="opt-in-instructions">Please select your preferences for receiving a client alert email summary</p>
                        <div className="opt-in-preferences-container">
                            <div className="opt-in-status-text-group">
                                <span className="text opt-in-status-title">Opt-In Status</span>
                                <span className="opt-in-status-value">
                                    {watchOptIn ? 'You are opted into client alert emails' : 'You are opted out of client alert emails'}
                                </span>
                            </div>
                            <div id="opt-in-toggle" className="button-switch-container opt-in-toggle-scaled">
                                <input type="checkbox" id="opt-in" {...register('optIn')} />
                                <div className="button-switch"></div>
                            </div>
                        </div>
                    </div>
                    {watchOptIn && (
                        <gds-show-hide id="opt-in-show-hide">
                            {addNewRecipientContainer()}
                            {fields.map((field, index) => (
                                existingRecipientContainer(field, index)
                            ))}
                        </gds-show-hide>
                    )}

                    <div className="row">
                        <div className="col-sm-12">
                            <button
                                type="submit"
                                className="btn btn--primary btn--full-mobile btn--pull-right"
                                disabled={!enableSaveButton}
                            >
                                <span>Save</span>
                            </button>
                            <button
                                type="button"
                                className="btn btn--destructive btn--full-mobile btn--pull-right expandable-form-cancel-btn"
                                disabled={!enableCancelButton}
                                onClick={onCancelClick}
                            >
                                <span>Cancel</span>
                            </button>
                        </div>
                    </div>
                </form>
            </div>

            <div id="cancel-confirm-modal" className="modal">
                <div className="modal-container">
                    <h2 className="modal-headline">Discard Changes</h2>
                    <div className="full-width-line" />
                    <div className="modal-content modal-content-with-cta-bar">
                        Are you sure you want to discard your changes?
                    </div>
                    <div className="modal-content">
                        <div className="modal--call-to-action-bar">
                            <button
                                type="button"
                                className="btn btn--primary btn--full-mobile btn--pull-right"
                                onClick={onReturnToForm}
                            >
                                <span>Keep Changes</span>
                            </button>
                            <button
                                type="button"
                                className="btn btn--destructive btn--full-mobile btn--pull-right"
                                onClick={onDiscardChanges}
                            >
                                <span>Discard Changes</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <gds-modal type="modal" ref={addRecipientModalRef} hide-header-close-button="">
                <span slot="header">Add New Recipient</span>
                <div>
                    <gds-input-wrapper size="large" error={addEmailErrorMsg ? "" : undefined}>
                        <gds-label-legend slot="label" element="label" for="add-email">Email Address</gds-label-legend>
                        <input
                            slot="input"
                            type="email"
                            id="edit-email"
                            autoComplete="email"
                            inputMode="email"
                            placeholder="Email Address"
                            value={newRecipient.email}
                            onChange={(e) => { setNewRecipient(prev => ({ ...prev, email: e.target.value })); setAddEmailErrorMsg(null); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') onAddRecipient(); }}
                        />
                        {addEmailErrorMsg && <span slot="error-message">{addEmailErrorMsg}</span>}
                    </gds-input-wrapper>
                    <gds-radio-button-group
                        ref={addRadioGroupRef}
                        group-id="add-frequency"
                        layout="grid"
                        fieldset-aria-labelledby="add-freq-legend"
                        error={addFreqError ? "" : undefined}
                    >
                        <span slot="legend">Frequency</span>
                        {addFreqError && <span slot="error-message">You must select at least one option before saving your preferences.</span>}
                        <gds-radio-button ref={dailyCheckboxRef} name="add-choice" value="Daily">
                            <span slot="label">Daily</span>
                        </gds-radio-button>
                        <gds-radio-button ref={biweeklyCheckboxRef} name="add-choice" value="Biweekly">
                            <span slot="label">Bi-weekly (Tuesday, Thursday)</span>
                        </gds-radio-button>
                    </gds-radio-button-group>
                </div>
                <gds-button appearance="primary" slot="buttons" onClick={onAddRecipient}>Add</gds-button>
                <gds-button appearance="secondary" slot="buttons" onClick={cancelAddRecipientModal}>Cancel</gds-button>
            </gds-modal>

            <gds-modal type="modal" ref={editRecipientModalRef} hide-header-close-button="">
                <div slot="header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <span>Edit Recipient</span>
                    <div className="client-email-preferences-action-buttons" style={{ marginRight: '-1.6rem' }}>
                        <gds-icon-button
                            icon-type="trash"
                            size="large"
                            style={{ '--icon-button-default-background-color': 'transparent' }}
                            onClick={() => { remove(editingIndex); onCancelEdit(); }}
                        />
                    </div>
                </div>
                <div>
                    <gds-input-wrapper size="large" error={editEmailErrorMsg ? "" : undefined}>
                        <gds-label-legend slot="label" element="label" for="edit-email">Email Address</gds-label-legend>
                        <input
                            slot="input"
                            type="email"
                            id="edit-email"
                            autoComplete="email"
                            inputMode="email"
                            placeholder="Email Address"
                            value={editingRecipient?.email ?? ''}
                            onChange={(e) => { setEditingRecipient(prev => ({ ...prev, email: e.target.value })); setEditEmailErrorMsg(null); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') onSaveEdit(editingIndex); }}
                        />
                        {editEmailErrorMsg && <span slot="error-message">{editEmailErrorMsg}</span>}
                    </gds-input-wrapper>
                    <gds-radio-button-group
                        ref={editRadioGroupRef}
                        group-id="edit-frequency"
                        layout="grid"
                        fieldset-aria-labelledby="edit-freq-legend"
                        error={editFreqError ? "" : undefined}
                    >
                        <span slot="legend">Frequency</span>
                        {editFreqError && <span slot="error-message">You must select at least one option before saving your preferences.</span>}
                        <gds-radio-button ref={editDailyRef} name="edit-choice" value="Daily">
                            <span slot="label">Daily</span>
                        </gds-radio-button>
                        <gds-radio-button ref={editBiweeklyRef} name="edit-choice" value="Biweekly">
                            <span slot="label">Bi-weekly (Tuesday, Thursday)</span>
                        </gds-radio-button>
                    </gds-radio-button-group>
                </div>
                <gds-button appearance="primary" slot="buttons" onClick={() => onSaveEdit(editingIndex)}>Update</gds-button>
                <gds-button appearance="secondary" slot="buttons" onClick={onCancelEdit}>Cancel</gds-button>
            </gds-modal>
        </Layout>
    );
}